import { useCallback, useMemo, useState } from "react";
import type { Dp, Filter } from "./ExState";
import { makeCol, StatsTable } from "../components/StatsTable2";
import { humanReadableCount, formatPct2 } from "common/src/T.bs";
import { IconButton } from "@mui/material";
import OpenInFull from "@mui/icons-material/OpenInFull";
import * as TraitImage from "common/src/tft-static/TraitImage";
import { make as TraitName0 } from "common/src/tft-static/TraitName0.bs";
import { traitRankUnitCount } from "common/src/tft-static/Sd";
import { make as SearchField } from "../components/SearchField2.bs";
import { searchTraits } from "../common/Search2";
import { make as Tooltip } from "common/src/components/Tooltip.bs";
import { make as TraitTooltip } from "common/src/tft-static/TraitTooltip.bs";
import { use as useSet } from "common/src/data/TftSet.bs";

const cols = [
  makeCol({
    width: 180,
    order: ([traitId], _, { traitsT }) => traitsT(traitId),
    defaultDesc: false,
    renderTitle: (_, { t }) => <> {t("Trait")} </>,
    renderValue: ([traitId, rank], _, { set }) => (
      <div className="flex items-center text-sm">
        <div className="flex-shrink-0">
          <TraitTooltip trait={traitId}>
            <TraitImage.PlainIcon traitId={traitId} rank={rank} size={28} />
          </TraitTooltip>
        </div>
        <div className="pl-[6px] font-roboto font-normal truncate">
          {traitRankUnitCount(traitId, rank, set) + " "}
          <TraitName0 trait={traitId} />
        </div>
      </div>
    ),
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Games")} </>,
    renderValue: ([_, __, v]) => <> {humanReadableCount(v.count)} </>,
  }),
  makeCol({
    width: 90,
    order: ([_, __, v]) => v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Play rate")} </>,
    tooltip: ([_, __, v]) => <> {humanReadableCount(v.count) + " games"} </>,
    renderValue: ([_, __, v], { baseCount }) => (
      <> {formatPct2(v.count / baseCount)} </>
    ),
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) => hidePlacements ? 0 : v.place / v.count,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Place")} </>,
    renderValue: ([_, __, v], { hidePlacements }) => <> {(hidePlacements ? "—" : (v.place / v.count).toFixed(2))} </>,
  }),
  makeCol({
    width: 76,
    order: ([_, __, v], { hidePlacements }) => hidePlacements ? 0 : v.delta,
    defaultDesc: false,
    alignRight: true,
    renderTitle: (_, { t }) => (
      <Tooltip
        title={
          "Delta shows the difference in average placement of the selected filter combination with the trait compared to the average placement of the filter combination without the trait. Lower is better."
        }
      >
        <div>{t("Delta")}</div>
      </Tooltip>
    ),
    renderValue: ([_, __, v], { hidePlacements }) => {
      return v.delta && !hidePlacements ? (
        <> {(v.delta > 0 ? "+" : "") + v.delta.toFixed(2)} </>
      ) : (
        <>—</>
      );
    },
  }),
  makeCol({
    width: 90,
    order: ([_, __, v], { hidePlacements }) => hidePlacements ? 0 : v.top4 / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Top 4")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => <> {hidePlacements ? "—" : formatPct2(v.top4 / v.count)} </>,
  }),
  makeCol({
    width: 80,
    order: ([_, __, v], { hidePlacements }) => hidePlacements ? 0 : v.won / v.count,
    alignRight: true,
    renderTitle: (_, { t }) => <> {t("Win")}% </>,
    renderValue: ([_, __, v], { hidePlacements }) => <> {hidePlacements ? "—" : formatPct2(v.won / v.count)} </>,
  }),
  makeCol({
    width: 48,
    alignRight: true,
    renderTitle: (_) => <div className="h-1" />,
    valueClass: "!justify-start",
    renderValue: ([traitId, rank], { onExpand, hideExpand }, { traitsT }) => {
      return hideExpand ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={(_) =>
              onExpand({
                typ: "t",
                id: traitId,
                sid: traitId,
                rank: rank,
                label: traitsT(traitId),
              })
            }
            classes={{
              root: "text-lg",
            }}
          >
            <OpenInFull fontSize="inherit" />
          </IconButton>
        </>
      );
    },
  }),
];

type Props = {
  data: Array<[string, number, Dp]>;
  baseCount: number;
  onExpand: (f: Filter) => void;
  hideExpand: boolean;
  minSample: number;
  hidePlacements: boolean;
};

const ExTraits = ({
  data,
  baseCount,
  onExpand,
  hideExpand,
  minSample,
  hidePlacements,
}: Props) => {
  const set = useSet();
  const [search, setSearch] = useState("");
  const keyFn = useCallback(
    ([id, starLevel, _]: [string, number, Dp]) => id + starLevel,
    []
  );
  const context = useMemo(() => ({ baseCount, onExpand, hideExpand, hidePlacements }), [
    baseCount,
    onExpand,
    hideExpand,
    hidePlacements,
  ]);

  const visibleFilter = useMemo(() => {
    if (search === "") {
      return () => true;
    }
    const passIds = searchTraits(search, set);
    return ([traitId]: [string]) => passIds.has(traitId);
  }, [search]);

  const filteredData = useMemo(
    () => data.filter(([_, _1, v]) => v.count >= minSample),
    [data, minSample]
  );

  if (data.length === 0) {
    return (
      <div className="px-5 py-6 text-xl text-medium text-white2">
        No trait combos with at least 100 games played
      </div>
    );
  } else {
    return (
      <>
        <div className="p-2">
          <SearchField search={search} setSearch={setSearch} />
        </div>
        <StatsTable
          cols={cols}
          context={context}
          data={filteredData}
          keyFn={keyFn}
          stickyColCount={1}
          defaultSortIdx={1}
          top="t47m92"
          visibleFilter={visibleFilter}
        />
      </>
    );
  }
};

export default ExTraits;
